import React from 'react';
import { Helmet } from 'react-helmet-async';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useTranslation } from '@shared/core/i18n';
import type { FaqItem } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';

type StationerySchemaProps = Readonly<{
  category: StationeryTemplateCategoryEnum;
  title: string;
  description: string;
  canonicalURL: string;
  faqItems: ReadonlyArray<FaqItem>;
}>;

const useBuildBreadcrumbList = (category: StationeryTemplateCategoryEnum, canonicalURL: string) => {
  const { tq } = useTranslation('stationery');

  const categoryDisplayName = {
    [StationeryTemplateCategoryEnum.saveTheDate]: tq`Save the Date`,
    [StationeryTemplateCategoryEnum.invitation]: tq`Invitation`,
    [StationeryTemplateCategoryEnum.thankYou]: tq`Thank You`,
    [StationeryTemplateCategoryEnum.holiday]: tq`Holiday`,
    [StationeryTemplateCategoryEnum.enclosure]: tq`Enclosure`,
    [StationeryTemplateCategoryEnum.menu]: tq`Menu`,
    [StationeryTemplateCategoryEnum.napkin]: tq`Napkin`,
    [StationeryTemplateCategoryEnum.sign]: tq`Sign`,
    [StationeryTemplateCategoryEnum.monogram]: tq`Monogram`,
  }[category];

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalURL}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: tq`Home`,
        item: 'https://withjoy.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: tq`Cards`,
        item: 'https://withjoy.com/cards/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: tq`Wedding`,
        item: 'https://withjoy.com/cards/wedding/',
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: categoryDisplayName,
        item: canonicalURL,
      },
    ],
  };
};

const useBuildFaqPage = (faqItems: ReadonlyArray<FaqItem>, canonicalURL: string) => {
  if (faqItems.length === 0) return null;

  return {
    '@type': 'FAQPage',
    '@id': `${canonicalURL}#faq`,
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
};

export const StationerySchema: React.FC<StationerySchemaProps> = ({ category, title, description, canonicalURL, faqItems }) => {
  const breadcrumbList = useBuildBreadcrumbList(category, canonicalURL);
  const faqPage = useBuildFaqPage(faqItems, canonicalURL);

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://withjoy.com/#organization',
              name: 'Joy',
              url: 'https://withjoy.com/',
              // same as the one used by marcom
              logo: 'https://withjoy.com/assets/public/marcom-prod/legacy/favicon/android-icon-192x192.png?opt=aggressive&ver=2',
              sameAs: ['https://www.instagram.com/withjoy/', 'https://www.pinterest.com/withjoy/', 'https://www.facebook.com/withjoy/'],
            },
            {
              '@type': 'WebSite',
              '@id': 'https://withjoy.com/#website',
              url: 'https://withjoy.com/',
              name: 'Joy',
              publisher: { '@id': 'https://withjoy.com/#organization' },
            },
            {
              '@type': 'WebPage',
              '@id': `${canonicalURL}#webpage`,
              url: canonicalURL,
              name: title,
              description,
              isPartOf: { '@id': 'https://withjoy.com/#website' },
              breadcrumb: { '@id': `${canonicalURL}#breadcrumb` },
            },
            ...(breadcrumbList ? [breadcrumbList] : []),
            ...(faqPage ? [faqPage] : []),
          ],
        })}
      </script>
    </Helmet>
  );
};
